import { useCallback, useRef } from 'react'
import { useTenant } from '@emerald-works-nova/auth'
import { useEvent } from '@emerald-works/react-event-bus-client'
import { useEffectWhen } from '../useEffectWhen'
import { useSelector } from 'react-redux'
import {
  ALLOWED_PLAYER_EVENTS,
  getPlayDuration,
  getPlayableMediaType,
  shouldTrackPause
} from './utils'
import ScrollStatement from './statements/scroll-statement'
import PageViewStatement from './statements/page-view-statement'
import PlayStatement from './statements/play-statement'
import { contentSlice } from '../../reducers'

export const useXapi = (content) => {
  const tenant = useTenant()
  const isLoadingExtraInfo = useSelector(contentSlice.selectors.selectIsLoadingExtraInfo)
  const xapiIsEnabled = tenant?.features?.xapi?.enabled && content && !isLoadingExtraInfo

  const timerRefs = useRef({
    awardedTime: 0,
    engagementTime: 0,
    engagementTimeId: null,
    idleTime: 0,
    idleTimeId: null
  })
  const scrollPositionRef = useRef({
    scrollHeight: 0,
    maxScrollPosition: 0
  })
  const engagingRef = useRef(false)
  const sentEvent = useRef({
    scroll: null,
    videoPaused: null,
    videoCompleted: null,
    pageView: null
  })
  const mediaPlaybackTime = useRef({
    position: 0,
    duration: 0
  })

  const [recordXapiActivity] = useEvent([
    {
      eventName: 'recordXapiActivity',
      onError: (error) => {
        console.log('Failed to send XAPI statement', error)
      }
    }
  ])

  const sendXapiStatement = useCallback(
    (statement) => {
      const payload = {
        content: { ...content, body: undefined },
        statement
      }

      recordXapiActivity.trigger(payload)
    },
    [recordXapiActivity, content]
  )

  const sendScrollXapiStatement = useCallback(() => {
    const statement = ScrollStatement.create({
      content,
      tenant,
      playableMediaType: getPlayableMediaType(document),
      playDuration: getPlayDuration(mediaPlaybackTime.current),
      engagementTime: timerRefs.current.engagementTime,
      scrollPosition: scrollPositionRef.current.maxScrollPosition,
      scrollHeight: scrollPositionRef.current.scrollHeight
    })

    sendXapiStatement(statement)
  }, [content, tenant, sendXapiStatement])

  const updateIdleTimer = useCallback(() => {
    timerRefs.current.idleTimeRef = 0
    const idleTimerId = timerRefs.current.idleTimeId = setInterval(() => {
      timerRefs.current.idleTimeRef = timerRefs.current.idleTimeRef + 1
      if (engagingRef.current) {
        clearInterval(idleTimerId)
        return
      }

      if (timerRefs.current.idleTimeRef > 180) {
        clearInterval(idleTimerId)

        if (sentEvent.current.scroll) {
          sendScrollXapiStatement()
        }
      }
    }, 1000)
  }, [sendScrollXapiStatement])

  const updateTimer = useCallback(() => {
    const engagingTimerId = timerRefs.current.engagementTimeId = setInterval(() => {
      if (timerRefs.current.awardedTime > 0) {
        timerRefs.current.engagementTime = timerRefs.current.engagementTime + 1
        timerRefs.current.awardedTime = timerRefs.current.awardedTime - 1
      } else {
        engagingRef.current = false
        updateIdleTimer()
        clearInterval(engagingTimerId)
      }
    }, 1000)
  }, [updateIdleTimer])

  const awardTime = useCallback(
    (duration) => {
      if (duration > timerRefs.current.awardedTime) {
        timerRefs.current.awardedTime = duration
      }

      if (!engagingRef.current) {
        engagingRef.current = true
        updateTimer()
      }
      return true
    },
    [updateTimer]
  )

  const clearTimers = useCallback(() => {
    if (timerRefs.current.engagementTimeId) {
      clearInterval(timerRefs.current.engagementTimeId)
    }

    if (timerRefs.current.idleTimeId) {
      clearInterval(timerRefs.current.idleTimeId)
    }
    engagingRef.current = false
  }, [])

  // Track page engagement using mousemove, click and scroll HTML events.
  useEffectWhen(
    xapiIsEnabled,
    () => {
      const handleMouseMove = () => {
        const awarded = awardTime(10)

        if (!awarded) {
          document.body.removeEventListener('mousemove', handleMouseMove)
        }
      }

      const handleClick = () => {
        const awarded = awardTime(5)

        if (!awarded) {
          document.body.removeEventListener('click', handleClick)
        }
      }

      const handleScroll = () => {
        const awarded = awardTime(10)

        if (!awarded) {
          window.removeEventListener('scroll', handleScroll)
        }
      }

      document.body.addEventListener('mousemove', handleMouseMove)
      document.body.addEventListener('click', handleClick)
      window.addEventListener('scroll', handleScroll)

      return () => {
        document.body.removeEventListener('mousemove', handleMouseMove)
        document.body.removeEventListener('click', handleClick)
        window.removeEventListener('scroll', handleScroll)
      }
    },
    [awardTime]
  )

  // When a user scrolls half way through the content.
  useEffectWhen(xapiIsEnabled, () => {
    const handleScroll = () => {
      const scrollPosition = document.documentElement.scrollTop + window.innerHeight
      const halfwayPoint = document.documentElement.scrollHeight / 2

      if (scrollPositionRef.current.maxScrollPosition < scrollPosition) {
        scrollPositionRef.current.maxScrollPosition = scrollPosition
        scrollPositionRef.current.scrollHeight = document.documentElement.scrollHeight
      }

      if (
        sentEvent.current.scroll &&
        scrollPositionRef.current.maxScrollPosition >= document.documentElement.scrollHeight
      ) {
        window.removeEventListener('scroll', handleScroll)
      }

      // Check if scroll position is past halfway point
      if (scrollPosition >= halfwayPoint && !sentEvent.current.scroll) {
        sendScrollXapiStatement()

        sentEvent.current.scroll = Date.now()
      }
    }

    window.addEventListener('scroll', handleScroll)

    // Clean up function to remove scroll event listener when component unmounts
    return () => {
      // Remove the scroll event listener using the stored reference
      window.removeEventListener('scroll', handleScroll)
    }
  }, [sendScrollXapiStatement])

  // Page view XAPI scroll events.
  useEffectWhen(
    xapiIsEnabled && !sentEvent.current.pageView,
    () => {
      const statement = PageViewStatement.create({
        content,
        tenant
      })

      sendXapiStatement(statement)
      sentEvent.current.pageView = Date.now()
    },
    [content, tenant, sendXapiStatement]
  )

  // Handle xAPI video event for JWPlayer.
  useEffectWhen(
    xapiIsEnabled,
    () => {
      const handleVideoEvent = ({ origin, data: eventBody, isTrusted }) => {
        const sameOrigin = window.location.origin === origin && isTrusted
        const eventName = eventBody?.eventName
        const allowedEvents = Object.values(ALLOWED_PLAYER_EVENTS).includes(
          eventName
        )

        if (!(sameOrigin && allowedEvents)) {
          return false
        }

        // Handle video playing event.
        if (eventName === ALLOWED_PLAYER_EVENTS.PLAYING_VIDEO) {
          mediaPlaybackTime.current.position = eventBody?.data?.position
          mediaPlaybackTime.current.duration = eventBody?.data?.duration

          return true
        }

        // Handle video paused event.
        if (eventName === ALLOWED_PLAYER_EVENTS.PAUSED_VIDEO) {
          if (shouldTrackPause(sentEvent.current.videoPaused)) {
            const statement = PlayStatement.create({
              tenant,
              content,
              mediaType: getPlayableMediaType(document),
              playDuration: getPlayDuration(mediaPlaybackTime.current)
            })

            sendXapiStatement(statement)
            sentEvent.current.videoPaused = Date.now()
          }

          return true
        }

        // Handle completed video event.
        if (eventName === ALLOWED_PLAYER_EVENTS.COMPLETED_VIDEO) {
          if (!sentEvent.current.videoCompleted) {
            const statement = PlayStatement.create({
              tenant,
              content,
              mediaType: getPlayableMediaType(document),
              playDuration: getPlayDuration(mediaPlaybackTime.current)
            })

            sendXapiStatement(statement)
            sentEvent.current.videoCompleted = Date.now()
          }

          return true
        }
      }
      window.addEventListener('message', handleVideoEvent)

      return () => {
        window.removeEventListener('message', handleVideoEvent)
      }
    },
    [content, tenant, sendXapiStatement]
  )

  // Handle xAPI video for native video/audio player.
  useEffectWhen(
    xapiIsEnabled,
    () => {
      const videoPlayer = document.querySelector('.video-player')
      const audioPlayer = document.querySelector('.audio-player')
      const player = videoPlayer || audioPlayer

      const handlePause = () => {
        if (shouldTrackPause(sentEvent.current.videoPaused)) {
          const statement = PlayStatement.create({
            tenant,
            content,
            mediaType: getPlayableMediaType(document),
            playDuration: getPlayDuration(mediaPlaybackTime.current)
          })

          sendXapiStatement(statement)
          sentEvent.current.videoPaused = Date.now()
        }
      }

      const handleTimeUpdate = () => {
        mediaPlaybackTime.current.position = player.currentTime
        mediaPlaybackTime.current.duration = player.duration
      }

      const handleCompleted = () => {
        if (!sentEvent.current.videoCompleted) {
          const statement = PlayStatement.create({
            tenant,
            content,
            mediaType: getPlayableMediaType(document),
            playDuration: getPlayDuration(mediaPlaybackTime.current)
          })

          sendXapiStatement(statement)
          sentEvent.current.videoCompleted = Date.now()
        }
      }

      // Event listener for when the video/audio is paused.
      player?.addEventListener('pause', handlePause)
      // Event listener for when the video/audio ends.
      player?.addEventListener('ended', handleCompleted)
      // Event listener for tracking time updates.
      player?.addEventListener('timeupdate', handleTimeUpdate)

      return () => {
        // Unregister event listeners.
        player?.removeEventListener('pause', handlePause)
        player?.removeEventListener('ended', handleCompleted)
        player?.removeEventListener('timeupdate', handleTimeUpdate)
      }
    },
    [content, tenant, sendXapiStatement]
  )

  // Send experienced event when user navigates away.
  useEffectWhen(xapiIsEnabled,
    () => {
      return () => {
        sendScrollXapiStatement()
        clearTimers()
      }
    },
    [sendScrollXapiStatement, clearTimers]
  )

  // Send experienced event when user closes the tab.
  useEffectWhen(xapiIsEnabled && !!sentEvent.current.scroll,
    () => {
      const handleBeforeUnload = (event) => {
        sendScrollXapiStatement()
      }

      window.addEventListener('beforeunload', handleBeforeUnload)

      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload)
      }
    },
    [sendScrollXapiStatement]
  )

  // Send experienced event when user switches tabs.
  useEffectWhen(xapiIsEnabled && sentEvent.current.scroll,
    () => {
      const handleVisibilityChange = (event) => {
        if (!document.hidden) return
        sendScrollXapiStatement()
        clearTimers()
      }

      window.addEventListener('visibilitychange', handleVisibilityChange)

      return () => {
        window.removeEventListener('visibilitychange', handleVisibilityChange)
      }
    },
    [sendScrollXapiStatement, clearTimers]
  )
}
